/**
 * Capacitor Mobile Integration for SlotFleet
 * This file handles mobile-specific initialization and features
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

// Check if running on native platform
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'

/**
 * Initialize mobile-specific features
 */
export const initMobile = async () => {
  if (!isNative) return;

  try {
    // Configure status bar (dark mode)
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#000000' });

    // Hide splash screen after app loads
    await SplashScreen.hide();

    // Handle app URL open (deep links)
    App.addListener('appUrlOpen', (event) => {
      // Handle deep links here
      const path = new URL(event.url).pathname;
      if (path) {
        window.location.href = path;
      }
    });

    // Handle app state changes
    App.addListener('appStateChange', ({ isActive }) => {
    });

    // Handle back button on Android
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // Keyboard events
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
    });

  } catch (error) {
  }
};

/**
 * Haptic feedback for touch interactions
 */
export const hapticFeedback = async (style = 'light') => {
  if (!isNative) return;

  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    }[style] || ImpactStyle.Light;

    await Haptics.impact({ style: impactStyle });
  } catch (error) {
  }
};

/**
 * Vibrate for notifications
 */
export const vibrate = async () => {
  if (!isNative) return;
  try {
    await Haptics.vibrate();
  } catch (error) {
  }
};

/**
 * Get safe area insets for notched devices
 */
export const getSafeAreaInsets = () => {
  const style = getComputedStyle(document.documentElement);
  return {
    top: parseInt(style.getPropertyValue('--sat') || '0'),
    bottom: parseInt(style.getPropertyValue('--sab') || '0'),
    left: parseInt(style.getPropertyValue('--sal') || '0'),
    right: parseInt(style.getPropertyValue('--sar') || '0'),
  };
};

export default {
  isNative,
  platform,
  initMobile,
  hapticFeedback,
  vibrate,
  getSafeAreaInsets,
};
